import { useState, useEffect, useCallback, useRef } from 'react'
import { getRegisterPeriods, getRegisterChecks, getRegisterCheckDetail } from '../../api'
import './RegisterTab.css'
import EmployeeCell from '../CustomerPortal/Lifeline/EmployeeCell'

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmt(n) {
  if (n == null || n === 0) return '—'
  return '$' + Math.round(Math.abs(n)).toLocaleString()
}
function fmtNeg(n) {
  if (!n || n === 0) return '—'
  return '–$' + Math.round(Math.abs(n)).toLocaleString()
}
function fmtHrs(h) {
  if (!h || h === 0) return '—'
  return h.toFixed(2) + ' h'
}
function fmtDate(s) {
  if (!s) return '—'
  const d = new Date(s)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
function fmtRate(r) {
  if (!r || r === 0) return '—'
  return '$' + Number(r).toFixed(2) + '/h'
}

// ─── Pay period selector ───────────────────────────────────────────────────────
function PeriodSelector({ periods, selectedPeriod, onSelect }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  if (!selectedPeriod) return null
  const label = `${selectedPeriod.payGroup} · ${fmtDate(selectedPeriod.payDate)}`

  return (
    <div className="rg-period-selector" ref={ref}>
      <button className="rg-period-btn" onClick={() => setOpen(!open)}>
        <span className="rg-period-label">{label}</span>
        <span className="rg-period-meta">
          {selectedPeriod.periodStart !== selectedPeriod.periodEnd
            ? `${fmtDate(selectedPeriod.periodStart)} – ${fmtDate(selectedPeriod.periodEnd)}`
            : fmtDate(selectedPeriod.periodStart)}
          &nbsp;·&nbsp; {selectedPeriod.checks} checks
        </span>
        <span className="rg-period-chev">▾</span>
      </button>
      {open && (
        <div className="rg-period-dropdown">
          <div className="rg-period-dropdown-hdr">Select pay period</div>
          {periods.map((p, i) => (
            <div
              key={i}
              className={`rg-period-opt ${p.payDate === selectedPeriod.payDate && p.payGroup === selectedPeriod.payGroup ? 'active' : ''}`}
              onClick={() => { onSelect(p); setOpen(false) }}
            >
              <span className="rg-period-opt-pg">{p.payGroup}</span>
              <span className="rg-period-opt-date">{fmtDate(p.payDate)}</span>
              <span className="rg-period-opt-range">
                {p.periodStart !== p.periodEnd
                  ? `${fmtDate(p.periodStart)} – ${fmtDate(p.periodEnd)}`
                  : fmtDate(p.periodStart)}
              </span>
              <span className="rg-period-opt-checks">{p.checks} checks</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Totals bar ───────────────────────────────────────────────────────────────
function TotalsBar({ totals }) {
  if (!totals) return null
  return (
    <div className="rg-totals-bar">
      <div className="rg-tc">
        <div className="rg-tc-lbl">Gross wages</div>
        <div className="rg-tc-val">{fmt(totals.gross)}</div>
      </div>
      <div className="rg-tc">
        <div className="rg-tc-lbl">Regular</div>
        <div className="rg-tc-val">{fmt(totals.regular)}</div>
      </div>
      <div className="rg-tc amber">
        <div className="rg-tc-lbl">Overtime</div>
        <div className="rg-tc-val">{fmt(totals.overtime)}</div>
      </div>
      <div className="rg-tc red">
        <div className="rg-tc-lbl">EE deductions</div>
        <div className="rg-tc-val">{fmtNeg(totals.deductions)}</div>
      </div>
      <div className="rg-tc red">
        <div className="rg-tc-lbl">EE taxes</div>
        <div className="rg-tc-val">{fmtNeg(totals.taxes)}</div>
      </div>
      <div className="rg-tc purple">
        <div className="rg-tc-lbl">ER cost</div>
        <div className="rg-tc-val">{fmt(totals.erCost)}</div>
      </div>
      <div className="rg-tc green">
        <div className="rg-tc-lbl">Net pay</div>
        <div className="rg-tc-val">{fmt(totals.net)}</div>
      </div>
    </div>
  )
}

// ─── Expanded row ─────────────────────────────────────────────────────────────
function ExpandedRow({ emp }) {
  const earnLines = (emp.earnings || []).reduce((acc, e) => {
    const key = e.payCode + '|' + e.payDescription
    if (!acc[key]) acc[key] = { ...e, amount: 0, hours: 0, amountYtd: 0 }
    acc[key].amount += e.amount
    acc[key].hours += e.hours
    acc[key].amountYtd = Math.max(acc[key].amountYtd, e.amountYtd)
    return acc
  }, {})
  const earnings = Object.values(earnLines).filter(e => e.amount !== 0 || e.hours !== 0)
    .sort((a, b) => b.amount - a.amount)

  const eeDeds = (emp.deductions || []).filter(d => d.employeeAmount !== 0)
  const erDeds = (emp.deductions || []).filter(d => d.employerAmount !== 0)
  const eeTaxes = (emp.taxes || []).filter(t => !t.isEmployerTax && t.amount !== 0)
  const erTaxes = (emp.taxes || []).filter(t => t.isEmployerTax && t.amount !== 0)

  const totalEEDed = eeDeds.reduce((s, d) => s + d.employeeAmount, 0)
  const totalEETax = eeTaxes.reduce((s, t) => s + t.amount, 0)
  const totalERDed = erDeds.reduce((s, d) => s + d.employerAmount, 0)
  const totalERTax = erTaxes.reduce((s, t) => s + t.amount, 0)
  const totalER = totalERDed + totalERTax

  return (
    <tr className="rg-xrow">
      <td colSpan="20">
        <div className="rg-xinner">

          {/* Col 1: Earnings + W4 elections */}
          <div className="rg-xcol">
            <div className="rg-xs-title earn">Earnings</div>
            {earnings.map((e, i) => (
              <div key={i} className="rg-xl earn">
                <span className="rg-xl-lbl">
                  {e.payDescription}
                  {e.hours > 0 && <span className="rg-xl-hrs"> {e.hours.toFixed(2)} h</span>}
                  {e.payRate > 0 && <span className="rg-xl-rate"> @ ${e.payRate.toFixed(2)}</span>}
                </span>
                <span className="rg-xl-val">{fmt(e.amount)}</span>
              </div>
            ))}
            <div className="rg-xl earn sub-tot">
              <span className="rg-xl-lbl">Gross wages</span>
              <span className="rg-xl-val">{fmt(emp.grossWagesCurrent)}</span>
            </div>
            <div className="rg-xl-ytd">
              <span>YTD gross</span><span>{fmt(earnings.reduce((s,e)=>s+e.amountYtd,0))}</span>
            </div>

            <div className="rg-xs-divider" />
            <div className="rg-xs-title elec">W-4 / Tax elections</div>
            <div className="rg-elec-grid">
              <div className="rg-elec-item">
                <span>Federal allowances</span>
                <span>{emp.federalAllowancesClaimed ?? 0}</span>
              </div>
              <div className="rg-elec-item">
                <span>Fed addl withholding</span>
                <span>{emp.federalAdditionalAmountWithheld > 0 ? '$' + emp.federalAdditionalAmountWithheld : '—'}</span>
              </div>
              <div className="rg-elec-item">
                <span>State (resident)</span>
                <span>{emp.stateOrProvince || '—'} · {emp.residentStateAllowancesClaimed ?? 0} allow.</span>
              </div>
              <div className="rg-elec-item">
                <span>State (work)</span>
                <span>{emp.stateOrProvince || '—'} · {emp.workStateAllowancesClaimed ?? 0} allow.</span>
              </div>
              <div className="rg-elec-item">
                <span>Pay rate</span>
                <span>{fmtRate(emp.payRate)}</span>
              </div>
              <div className="rg-elec-item">
                <span>SSN</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{emp.ssn || 'xxx-xx-xxxx'}</span>
              </div>
            </div>
          </div>

          {/* Col 2: EE deductions */}
          <div className="rg-xcol">
            <div className="rg-xs-title ded">EE deductions</div>
            {eeDeds.length === 0 && <div className="rg-xl-empty">No EE deductions</div>}
            {eeDeds.map((d, i) => (
              <div key={i} className="rg-xl ded">
                <span className="rg-xl-lbl">
                  {d.deductionDescription}
                  {d.preTax && <span className="rg-xl-badge pre-tax">pre-tax</span>}
                  {d.isPension && <span className="rg-xl-badge pension">retirement</span>}
                </span>
                <span className="rg-xl-val">{fmtNeg(d.employeeAmount)}</span>
              </div>
            ))}
            {eeDeds.length > 0 && (
              <>
                <div className="rg-xl ded sub-tot">
                  <span className="rg-xl-lbl">Total EE deductions</span>
                  <span className="rg-xl-val">{fmtNeg(totalEEDed)}</span>
                </div>
                <div className="rg-xl-ytd">
                  <span>YTD EE deductions</span>
                  <span>{fmtNeg(eeDeds.reduce((s,d)=>s+d.employeeAmountYtd,0))}</span>
                </div>
              </>
            )}
          </div>

          {/* Col 3: EE taxes */}
          <div className="rg-xcol">
            <div className="rg-xs-title tax">EE taxes</div>
            {eeTaxes.map((t, i) => (
              <div key={i} className="rg-xl tax">
                <span className="rg-xl-lbl">
                  {t.taxDescription}
                  {t.basisAmount > 0 && <span className="rg-xl-basis"> (basis {fmt(t.basisAmount)})</span>}
                </span>
                <span className="rg-xl-val">{fmtNeg(t.amount)}</span>
              </div>
            ))}
            {eeTaxes.length > 0 && (
              <>
                <div className="rg-xl tax sub-tot">
                  <span className="rg-xl-lbl">Total EE taxes</span>
                  <span className="rg-xl-val">{fmtNeg(totalEETax)}</span>
                </div>
                <div className="rg-xl-ytd">
                  <span>YTD EE taxes</span>
                  <span>{fmtNeg(eeTaxes.reduce((s,t)=>s+t.amountYtd,0))}</span>
                </div>
              </>
            )}
            <div className="rg-xl net-line">
              <span className="rg-xl-lbl">Net pay</span>
              <span className="rg-xl-val net">{fmt(emp.netPayCurrent)}</span>
            </div>
          </div>

          {/* Col 4: ER cost */}
          <div className="rg-xcol">
            <div className="rg-xs-title er">ER benefits cost</div>
            {erDeds.length === 0 && <div className="rg-xl-empty">No ER benefit contributions</div>}
            {erDeds.map((d, i) => (
              <div key={i} className="rg-xl er">
                <span className="rg-xl-lbl">
                  {d.deductionDescription}
                  {d.isPension && <span className="rg-xl-badge pension">retirement</span>}
                </span>
                <span className="rg-xl-val">{fmt(d.employerAmount)}</span>
              </div>
            ))}
            {erDeds.length > 0 && (
              <div className="rg-xl-ytd">
                <span>YTD ER benefits</span>
                <span>{fmt(erDeds.reduce((s,d)=>s+d.employerAmountYtd,0))}</span>
              </div>
            )}

            <div className="rg-xs-divider" />
            <div className="rg-xs-title er">ER payroll taxes</div>
            {erTaxes.map((t, i) => (
              <div key={i} className="rg-xl er">
                <span className="rg-xl-lbl">{t.taxDescription}</span>
                <span className="rg-xl-val">{fmt(t.amount)}</span>
              </div>
            ))}
            {(erDeds.length > 0 || erTaxes.length > 0) && (
              <div className="rg-xl er sub-tot">
                <span className="rg-xl-lbl">Total ER cost</span>
                <span className="rg-xl-val">{fmt(totalER)}</span>
              </div>
            )}

            <div className="rg-xl total-emp-cost">
              <span className="rg-xl-lbl">Total cost of employment</span>
              <span className="rg-xl-val">{fmt(emp.grossWagesCurrent + totalER)}</span>
            </div>
          </div>

        </div>
      </td>
    </tr>
  )
}

// ─── Main register table ───────────────────────────────────────────────────────
function RegisterTable({ customerId, rows, split, search, onOpenEmployee = null }) {
  const [expanded, setExpanded] = useState(new Set())
  const [expandData, setExpandData] = useState({})
  const [loadingExpand, setLoadingExpand] = useState(new Set())
  const [expandError, setExpandError] = useState({})

  useEffect(() => {
    setExpanded(new Set())
    setExpandData({})
    setExpandError({})
  }, [rows])

  const toggleExpand = useCallback(async (payIdentifier, row) => {
    const next = new Set(expanded)
    if (next.has(payIdentifier)) {
      next.delete(payIdentifier)
      setExpanded(next)
      return
    }
    next.add(payIdentifier)
    setExpanded(next)
    if (expandData[payIdentifier]) return

    setLoadingExpand(prev => new Set([...prev, payIdentifier]))
    setExpandError(prev => { const n = { ...prev }; delete n[payIdentifier]; return n })
    try {
      const d = await getRegisterCheckDetail(customerId, payIdentifier)
      setExpandData(prev => ({
        ...prev,
        [payIdentifier]: {
          ...row,
          earnings: d.earnings || [],
          deductions: d.deductions || [],
          taxes: d.taxes || []
        }
      }))
    } catch (e) {
      console.error('Expand load failed', e)
      setExpandError(prev => ({ ...prev, [payIdentifier]: 'This check’s detail could not be loaded.' }))
    } finally {
      setLoadingExpand(prev => { const s = new Set(prev); s.delete(payIdentifier); return s })
    }
  }, [expanded, expandData, customerId])

  const hasSplit = !!split?.available
  const filtered = search
    ? rows.filter(r => r.fullName?.toLowerCase().includes(search.toLowerCase()) || r.employeeNumber?.includes(search))
    : rows

  if (!rows.length) {
    return <div className="rg-empty">No checks found for this pay period.</div>
  }

  return (
    <div className="rg-tbl-wrap">
      <table className="rg-table">
        <thead>
          <tr>
            <th className="rg-th L" style={{width:28}}></th>
            <th className="rg-th L" style={{minWidth:180}}>Employee</th>
            <th className="rg-th L">Check ID</th>
            <th className="rg-th L">Rate</th>
            <th className="rg-th ge L">Gross</th>
            <th className="rg-th ge">{hasSplit ? 'Reg hrs' : 'Hours'}</th>
            <th className="rg-th ge">OT hrs</th>
            <th className="rg-th ge">OT $</th>
            <th className="rg-th de">EE ded</th>
            <th className="rg-th te">EE tax</th>
            <th className="rg-th ere">ER cost</th>
            <th className="rg-th ne">Net pay</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((row) => {
            const pid = row.payIdentifier
            const isOpen = expanded.has(pid)
            const isLoadingX = loadingExpand.has(pid)
            const xData = expandData[pid]
            const xErr = expandError[pid]

            return [
              <tr
                key={pid}
                className={`rg-row ${isOpen ? 'open' : ''}`}
                onClick={() => toggleExpand(pid, row)}
              >
                <td className="rg-td chev-cell L">
                  {isLoadingX
                    ? <span className="rg-spinner-sm" />
                    : <span className={`rg-chev ${isOpen ? 'open' : ''}`}>›</span>
                  }
                </td>
                <td className="rg-td L">
                  <div className="rg-emp-name">{row.fullName}</div>
                  <div className="rg-emp-meta">
                    <EmployeeCell number={row.employeeNumber} employeeRef={row.employeeRef} onOpenEmployee={onOpenEmployee} />
                    {row.location && <> · {row.location}</>}
                    {row.job && <> · {row.job}</>}
                  </div>
                </td>
                <td className="rg-td L rg-mono rg-mu">{row.payIdentifier}</td>
                <td className="rg-td rg-mu">{fmtRate(row.payRate)}</td>
                <td className="rg-td ge L"><strong>{fmt(row.grossWagesCurrent)}</strong></td>
                <td className="rg-td ge">{fmtHrs(hasSplit ? row.regHours : row.totalHours)}</td>
                <td className="rg-td ge">{fmtHrs(row.otHours)}</td>
                <td className="rg-td ge">{fmt(row.otAmount)}</td>
                <td className="rg-td de">{fmtNeg(row.deductionsCurrent)}</td>
                <td className="rg-td te">{fmtNeg(row.taxesCurrent)}</td>
                <td className="rg-td ere">{fmt(row.erCost)}</td>
                <td className="rg-td ne">{fmt(row.netPayCurrent)}</td>
              </tr>,
              isOpen && xData && <ExpandedRow key={`${pid}-x`} emp={xData} />,
              isOpen && !xData && !isLoadingX && (
                <tr key={`${pid}-loading`} className="rg-xrow">
                  <td colSpan="12">
                    {xErr
                      ? <div className="rg-xloading">{xErr}</div>
                      : <div className="rg-xloading"><div className="rg-spinner" /> Loading detail…</div>}
                  </td>
                </tr>
              )
            ]
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── Root component ────────────────────────────────────────────────────────────
export default function RegisterTab({ customerId, onOpenEmployee = null }) {
  const [periods, setPeriods] = useState([])
  const [periodsState, setPeriodsState] = useState({ loading: true, message: null })
  const [selectedPeriod, setSelectedPeriod] = useState(null)
  const [search, setSearch] = useState('')
  const [register, setRegister] = useState(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)

  useEffect(() => {
    let live = true
    setPeriodsState({ loading: true, message: null })
    setSelectedPeriod(null)
    setRegister(null)
    getRegisterPeriods(customerId)
      .then(data => {
        if (!live) return
        if (data.available === false) {
          setPeriods([])
          setPeriodsState({ loading: false, message: data.reason || 'The register is not available for this customer.' })
          return
        }
        const list = (data.periods || []).map(r => ({
          payDate: r.payDate, payGroup: r.payGroup,
          periodStart: r.periodStart, periodEnd: r.periodEnd,
          checks: r.checks, freq: r.payPeriodFrequency
        }))
        setPeriods(list)
        setPeriodsState({ loading: false, message: list.length ? null : 'No pay periods in the last year.' })
        if (list.length > 0) setSelectedPeriod(list[0])
      })
      .catch(e => {
        console.error('Periods load failed', e)
        if (live) setPeriodsState({ loading: false, message: 'The register could not be loaded.' })
      })
    return () => { live = false }
  }, [customerId])

  useEffect(() => {
    if (!selectedPeriod) return
    let live = true
    setLoading(true)
    setMessage(null)
    getRegisterChecks(customerId, selectedPeriod.payDate, selectedPeriod.payGroup)
      .then(data => {
        if (!live) return
        if (data.available === false) {
          setRegister(null)
          setMessage(data.reason || 'The register is not available for this customer.')
        } else {
          setRegister(data)
        }
      })
      .catch(e => {
        console.error('Register load failed', e)
        if (live) { setRegister(null); setMessage('This pay period could not be loaded.') }
      })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [selectedPeriod, customerId])

  const split = register?.split

  return (
    <div className="rg-root">
      <div className="rg-header">
        <div className="rg-header-left">
          <div className="rg-title">Payroll Register</div>
          <div className="rg-sub">
            {selectedPeriod
              ? `${selectedPeriod.payGroup} · Period ${fmtDate(selectedPeriod.periodStart)} – ${fmtDate(selectedPeriod.periodEnd)}`
              : (periodsState.loading ? 'Loading…' : '—')}
          </div>
        </div>
        <div className="rg-header-right">
          <PeriodSelector periods={periods} selectedPeriod={selectedPeriod} onSelect={setSelectedPeriod} />
          <input
            className="rg-search"
            placeholder="Search employee…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button className="rg-action-btn">Export CSV</button>
        </div>
      </div>

      <TotalsBar totals={register?.totals} />

      {split && split.available === false && (
        <div className="rg-note">{split.reason}</div>
      )}
      {register?.truncated && (
        <div className="rg-note">Showing the first {register.rows.length.toLocaleString()} checks of this pay period; totals cover those checks.</div>
      )}

      {!periodsState.loading && periodsState.message && (
        <div className="rg-empty">{periodsState.message}</div>
      )}
      {selectedPeriod && loading && (
        <div className="rg-loading">
          <div className="rg-spinner" />
          Loading register…
        </div>
      )}
      {selectedPeriod && !loading && message && <div className="rg-empty">{message}</div>}
      {selectedPeriod && !loading && register && (
        <RegisterTable
          customerId={customerId}
          rows={register.rows || []}
          split={split}
          search={search}
          onOpenEmployee={onOpenEmployee}
        />
      )}

      <div className="rg-footer">
        <span className="rg-footer-hint">Click any row to expand earnings, deductions, taxes, and ER cost detail</span>
        <span className="rg-footer-sync">Data via UKG Pro API</span>
      </div>
    </div>
  )
}
